import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { api, type CantidadesFacturables, type Cuenta, type ResumenDiario as Resumen, type Tarifas } from '../api';
import { mensajeDeError, useApi } from '../lib/useApi';
import { useEsCelular } from '../lib/useMedia';
import { CURVA, desplegar, fila } from '../components/movimiento';
import { Cargando, Encabezado, Icono, Numero, PanelError, Vacio, useConfirmar } from '../components/ui';
import { BarraFila, PALETA } from '../components/graficos';
import { aviso, botonSecundario, c, campo, etiquetaCampo, filaTabla, miles, mono, nota } from '../theme';
import { Bloque, CAMPOS_TARIFA, Pesos, TablaFactura, diaLargo, rejilla, tituloDe } from './ResumenDiario';

/**
 * La pestaña de prueba de la facturación, sólo para administración.
 *
 * Arranca con los números reales de la quincena y deja cambiar tarifas y
 * cantidades para ver cómo se mueve la cuenta, sin guardar nada. La cuenta la
 * hace el servidor con la misma función que factura la quincena de verdad: lo
 * que se prueba acá es lo que después se cobra.
 */

const CAMPOS_CANTIDAD: {
  clave: keyof CantidadesFacturables;
  rotulo: string;
  ayuda: string;
}[] = [
  {
    clave: 'palletsIngresados',
    rotulo: 'Pallets ingresados',
    ayuda: 'Van a la descarga.',
  },
  {
    clave: 'capachosEntregados',
    rotulo: 'Capachos entregados',
    ayuda: 'Van a la carga y al retrabajo.',
  },
  {
    clave: 'palletsDespachados',
    rotulo: 'Pallets despachados completos',
    ayuda: 'Van a la carga, sin retrabajo.',
  },
  {
    clave: 'picoPallets',
    rotulo: 'Pallets del día con más pallets',
    ayuda: 'Van al almacenaje.',
  },
];

type Formulario = Record<keyof Tarifas | keyof CantidadesFacturables, string>;

/**
 * Un campo que el servidor rechazaría: vacío —el que se borró para volver a
 * tipearlo—, negativo, o con decimales en una cantidad. Con alguno así no se
 * pide la cuenta: queda a la vista la última que dio.
 */
function invalido(k: keyof Formulario, v: string): boolean {
  const x = Number(v);
  if (v.trim() === '' || !Number.isFinite(x) || x < 0) return true;
  return CAMPOS_CANTIDAD.some((cc) => cc.clave === k) && !Number.isInteger(x);
}

function formularioDe(r: Resumen): Formulario | null {
  const f = r.facturacion;
  if (!f) return null;
  return {
    cargaDescarga: String(f.tarifas.cargaDescarga),
    movimientoZorra: String(f.tarifas.movimientoZorra),
    retrabajo: String(f.tarifas.retrabajo),
    almacenaje: String(f.tarifas.almacenaje),
    palletsIngresados: String(r.kpis.palletsIngresados),
    capachosEntregados: String(r.kpis.capachosEntregados),
    palletsDespachados: String(r.kpis.palletsDespachados),
    picoPallets: String(f.pico.pallets),
  };
}

function Campo({
  rotulo,
  ayuda,
  valor,
  real,
  entero,
  malo,
  onCambio,
}: {
  rotulo: string;
  ayuda: string;
  valor: string;
  real: string;
  entero?: boolean;
  malo: boolean;
  onCambio: (v: string) => void;
}) {
  const cambiado = valor !== real;
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={etiquetaCampo}>{rotulo}</span>
      <input
        type="number"
        inputMode={entero ? 'numeric' : 'decimal'}
        min={0}
        step={entero ? 1 : 0.01}
        value={valor}
        onChange={(e) => onCambio(e.target.value)}
        aria-invalid={malo}
        style={{ ...campo, borderColor: malo ? c.rojo : cambiado ? c.ambar : undefined, transition: 'border-color .25s' }}
      />
      <span style={{ fontSize: 12, color: malo ? c.rojo : cambiado ? c.ambar : c.texto3, transition: 'color .25s' }}>
        {malo ? (entero ? 'Tiene que ser un número entero, cero o más. ' : 'Tiene que ser un número, cero o más. ') : ''}
        {cambiado ? `Real: ${real}. ` : ''}
        {ayuda}
      </span>
    </label>
  );
}

export default function PruebaFacturacion() {
  const celular = useEsCelular();
  const [dia, setDia] = useState('');
  // Hacia dónde se movió el selector: el título nuevo entra desde ese lado.
  const sentido = useRef(1);
  const { datos, cargando, error, recargar } = useApi<Resumen>(() => api.resumen('quincena', dia || undefined), [dia]);

  const real = datos ? formularioDe(datos) : null;
  const [form, setForm] = useState<Formulario | null>(null);
  const [cuenta, setCuenta] = useState<Cuenta | null>(null);
  const [errorCuenta, setErrorCuenta] = useState<string | null>(null);
  const [confirmar, dialogo] = useConfirmar();

  // Cada quincena nueva arranca de sus números reales.
  useEffect(() => {
    if (datos) setForm(formularioDe(datos));
  }, [datos]);

  // La cuenta se rehace sola, 400 ms después de la última tecla: un pedido
  // por tecla es tráfico al pedo y hace titilar la factura. Con un campo
  // inválido no se pide nada y queda la última cuenta. La respuesta vieja que
  // llega tarde se descarta.
  useEffect(() => {
    if (!form) return;
    if ((Object.keys(form) as (keyof Formulario)[]).some((k) => invalido(k, form[k]))) return;
    let vigente = true;
    const num = (k: keyof Formulario) => Number(form[k]);
    const espera = setTimeout(() => {
      api
        .simularFacturacion(
          {
            cargaDescarga: num('cargaDescarga'),
            movimientoZorra: num('movimientoZorra'),
            retrabajo: num('retrabajo'),
            almacenaje: num('almacenaje'),
          },
          {
            palletsIngresados: num('palletsIngresados'),
            capachosEntregados: num('capachosEntregados'),
            palletsDespachados: num('palletsDespachados'),
            picoPallets: num('picoPallets'),
          },
        )
        .then((r) => {
          if (!vigente) return;
          setCuenta(r);
          setErrorCuenta(null);
        })
        .catch((err) => vigente && setErrorCuenta(mensajeDeError(err)));
    }, 400);
    return () => {
      vigente = false;
      clearTimeout(espera);
    };
  }, [form]);

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (!datos) return <Cargando texto="Armando la quincena…" />;
  if (!datos.facturacion)
    return (
      <Vacio
        icono="receipt_long"
        titulo="Sin cuenta para esta quincena"
        detalle="El servidor no devolvió la facturación de la quincena, así que no hay números reales de los que arrancar."
        accion={
          <button type="button" onClick={recargar} style={botonSecundario}>
            Reintentar
          </button>
        }
      />
    );
  if (!form || !real) return <Cargando texto="Armando la quincena…" />;

  const f = datos.facturacion;
  const maxOcupacion = Math.max(...f.ocupacion.map((d) => d.pallets), 1);
  const diferencia = cuenta ? cuenta.total - f.total : 0;
  const cambiado = (Object.keys(form) as (keyof Formulario)[]).some((k) => form[k] !== real[k]);
  const hayInvalido = (Object.keys(form) as (keyof Formulario)[]).some((k) => invalido(k, form[k]));

  // Cambiar de quincena vuelve a los números reales de la otra: lo editado se
  // pierde, así que se pregunta.
  async function irA(d: string | null) {
    if (!d) return;
    if (
      cambiado &&
      !(await confirmar({
        titulo: '¿Cambiar de quincena?',
        detalle: 'Los valores que cambiaste en esta prueba se descartan y la otra quincena arranca con sus números reales.',
        boton: 'Cambiar y descartar lo editado',
      }))
    )
      return;
    sentido.current = d === datos?.siguiente ? 1 : -1;
    setDia(d);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Encabezado
        bajada="Los números reales de la quincena, para cambiarlos y ver cómo se mueve la cuenta. Nada de esto se guarda."
      />

      <div style={nota}>
        Es una pestaña de prueba y sólo la ven los administradores. Las tarifas de verdad se cambian en Resumen ›
        Quincena › Editar tarifas.
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={() => void irA(datos.anterior)}
          disabled={datos.anterior === null}
          aria-label="Quincena anterior"
          style={{ ...botonSecundario, padding: '10px 14px', opacity: datos.anterior === null ? 0.45 : 1 }}
        >
          <Icono nombre="chevron_left" size={20} />
        </button>
        <span style={{ fontSize: 15, fontWeight: 600, color: c.texto, ...(celular ? { flex: 1, textAlign: 'center' as const } : {}) }}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={tituloDe(datos)}
              initial={{ opacity: 0, x: 18 * sentido.current }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -18 * sentido.current }}
              transition={{ duration: 0.22, ease: CURVA }}
              style={{ display: 'inline-block' }}
            >
              {tituloDe(datos)}
            </motion.span>
          </AnimatePresence>
        </span>
        <button
          type="button"
          onClick={() => void irA(datos.siguiente)}
          disabled={datos.siguiente === null}
          aria-label="Quincena siguiente"
          style={{
            ...botonSecundario,
            padding: '10px 14px',
            opacity: datos.siguiente === null ? 0.45 : 1,
          }}
        >
          <Icono nombre="chevron_right" size={20} />
        </button>
        <AnimatePresence>
          {cargando && (
            <motion.span
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              style={{ fontSize: 12.5, color: c.texto3 }}
              role="status"
            >
              Actualizando…
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Uno arriba del otro: lado a lado, la cuenta quedaba angosta y la
          factura no entraba. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Bloque
          titulo="Tarifas y cantidades"
          bajada="Arrancan con los valores reales. Lo que cambies se marca en ámbar, con el valor real abajo."
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={rejilla(180)}>
              {CAMPOS_TARIFA.map((t) => (
                <Campo
                  key={t.clave}
                  rotulo={t.rotulo}
                  ayuda={t.ayuda}
                  valor={form[t.clave]}
                  real={real[t.clave]}
                  malo={invalido(t.clave, form[t.clave])}
                  onCambio={(v) => setForm({ ...form, [t.clave]: v })}
                />
              ))}
            </div>
            <div style={rejilla(180)}>
              {CAMPOS_CANTIDAD.map((t) => (
                <Campo
                  key={t.clave}
                  rotulo={t.rotulo}
                  ayuda={t.ayuda}
                  valor={form[t.clave]}
                  real={real[t.clave]}
                  entero
                  malo={invalido(t.clave, form[t.clave])}
                  onCambio={(v) => setForm({ ...form, [t.clave]: v })}
                />
              ))}
            </div>
            <div>
              <button
                type="button"
                disabled={!cambiado}
                onClick={() => setForm(real)}
                style={{ ...botonSecundario, opacity: cambiado ? 1 : 0.45 }}
              >
                <Icono nombre="restart_alt" size={18} />
                Volver a los datos reales
              </button>
            </div>
          </div>
        </Bloque>

        <Bloque
          titulo="La cuenta"
          bajada={
            cambiado && cuenta ? (
              <>
                Con los datos reales da <Pesos valor={f.total} />;{' '}
                <strong style={{ color: diferencia >= 0 ? c.verde : c.ambar, transition: 'color .25s' }}>
                  la prueba da {diferencia >= 0 ? '+' : '−'}
                  <Pesos valor={Math.abs(diferencia)} />
                </strong>
                .
              </>
            ) : (
              'Con los datos reales de la quincena, tal como sale en el resumen.'
            )
          }
          cuerpo={false}
        >
          <AnimatePresence initial={false}>
            {(hayInvalido || errorCuenta) && (
              <motion.div key="aviso" {...desplegar}>
                <div style={{ padding: '0 18px 12px' }}>
                  <div style={aviso(errorCuenta ? 'err' : 'warn')}>
                    {errorCuenta ?? 'Hay un campo vacío o inválido (marcado en rojo): se muestra la última cuenta.'}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {cuenta ? (
            <TablaFactura f={cuenta} />
          ) : (
            <Cargando texto="Calculando…" />
          )}
        </Bloque>
      </div>

      <Bloque
        titulo="Pallets en el depósito, día por día"
        bajada={
          <>
            De acá sale el almacenaje: se cobra sobre el día con más pallets,{' '}
            <strong style={{ color: c.texto }}>
              {diaLargo(f.pico.dia)} con {miles(f.pico.pallets)}
            </strong>
            . Un pallet cuenta desde que entró hasta el día de su última entrega, si ya no le queda nada arriba.
          </>
        }
        cuerpo={false}
      >
        {f.ocupacion.map((d, i) => {
          const esPico = d.dia === f.pico.dia;
          return (
            <motion.div
              key={d.dia}
              {...fila(i)}
              // En el celular la fecha pasa arriba y la barra con el número abajo.
              style={{ ...filaTabla, fontWeight: esPico ? 600 : undefined, ...(celular ? { flexWrap: 'wrap', gap: 6 } : {}) }}
            >
              <span
                style={{
                  width: celular ? '100%' : 200,
                  fontFamily: mono,
                  fontSize: 12.5,
                  color: esPico ? c.texto : c.texto2,
                }}
              >
                {diaLargo(d.dia)}
              </span>
              <span style={{ flex: 1, minWidth: 120 }}>
                <BarraFila
                  valor={d.pallets}
                  maximo={maxOcupacion}
                  color={esPico ? PALETA.falta : PALETA.entra}
                  descripcion={`${diaLargo(d.dia)}: ${miles(d.pallets)} pallets`}
                  retraso={i * 0.035}
                />
              </span>
              <span
                style={{
                  width: celular ? 'auto' : 110,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <Numero valor={d.pallets} /> {esPico ? '· pico' : ''}
              </span>
            </motion.div>
          );
        })}
      </Bloque>
      {dialogo}
    </div>
  );
}
